import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown } from 'lucide-react';
import { format, isBefore, startOfDay } from 'date-fns';
import CalendarComponent from 'react-calendar';
import 'react-calendar/dist/Calendar.css';

// Custom styles for react-calendar to match design system
const calendarStyles = `
  .react-calendar {
    width: 100%;
    max-width: 100%;
    background: transparent;
    border: none;
    font-family: inherit;
  }
  
  .react-calendar__navigation {
    display: flex;
    margin-bottom: 1rem;
  }
  
  .react-calendar__navigation button {
    min-width: 44px;
    background: none;
    border: none;
    font-size: 16px;
    font-weight: 500;
    color: hsl(var(--foreground));
    cursor: pointer;
    padding: 8px;
    border-radius: 6px;
    transition: all 0.2s;
  }
  
  .react-calendar__navigation button:hover {
    background-color: hsl(var(--accent));
    color: hsl(var(--accent-foreground));
  }
  
  .react-calendar__navigation button:disabled {
    background-color: transparent;
    color: hsl(var(--muted-foreground));
    cursor: not-allowed;
  }
  
  .react-calendar__month-view__weekdays {
    text-align: center;
    text-transform: uppercase;
    font-weight: 500;
    font-size: 0.75rem;
    color: hsl(var(--muted-foreground));
    margin-bottom: 0.5rem;
  }
  
  .react-calendar__month-view__weekdays__weekday {
    padding: 0.5rem;
  }
  
  .react-calendar__month-view__days {
    display: grid !important;
    grid-template-columns: repeat(7, 1fr);
    gap: 2px;
  }
  
  .react-calendar__tile {
    max-width: 100%;
    padding: 8px;
    background: none;
    border: none;
    border-radius: 6px;
    font-size: 14px;
    font-weight: 500;
    color: hsl(var(--foreground));
    cursor: pointer;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 36px;
  }
  
  .react-calendar__tile:hover {
    background-color: hsl(var(--accent));
    color: hsl(var(--accent-foreground));
  }
  
  .react-calendar__tile--active {
    background-color: hsl(var(--primary));
    color: hsl(var(--primary-foreground));
  }
  
  .react-calendar__tile--active:hover {
    background-color: hsl(var(--primary));
    color: hsl(var(--primary-foreground));
  }
  
  .react-calendar__tile--now {
    background-color: hsl(var(--accent));
    color: hsl(var(--accent-foreground));
    font-weight: 600;
  }
  
  .react-calendar__tile--disabled {
    background-color: transparent;
    color: hsl(var(--muted-foreground));
    cursor: not-allowed;
    opacity: 0.5;
  }
  
  .react-calendar__tile--disabled:hover {
    background-color: transparent;
    color: hsl(var(--muted-foreground));
  }
  
  /* Mobile-specific styles */
  @media (max-width: 768px) {
    .react-calendar {
      width: 100vw;
      max-width: 100vw;
      padding: 1rem;
    }
    
    .react-calendar__tile {
      height: 48px;
      font-size: 16px;
    }
    
    .react-calendar__navigation button {
      min-width: 48px;
      height: 48px;
      font-size: 18px;
    }
  }
`;

export interface DatePickerProps {
  value: Date | undefined;
  onChange: (date: Date | undefined) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  minDate?: Date;
  maxDate?: Date;
  isMobile?: boolean;
}

/**
 * DatePicker Component
 * 
 * A reusable date picker component with an input field that opens a calendar popup on click.
 * 
 * Features:
 * - Input field with calendar popup
 * - Pre-filled with provided value
 * - Disabled dates support
 * - Customizable styling
 * - Outside click to close
 * - Keyboard navigation support
 * - Mobile-optimized positioning
 * 
 * @example
 * ```tsx
 * <DatePicker
 *   value={selectedDate}
 *   onChange={setSelectedDate}
 *   placeholder="Select a date"
 *   minDate={new Date()}
 *   isMobile={true}
 * />
 * ```
 */
export const DatePicker: React.FC<DatePickerProps> = ({ 
  value, 
  onChange, 
  placeholder = "Select date", 
  disabled = false,
  className = "",
  minDate = new Date(),
  maxDate,
  isMobile = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const calendarRef = useRef<HTMLDivElement>(null);

  // Close calendar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        calendarRef.current &&
        !calendarRef.current.contains(event.target as Node) &&
        !inputRef.current?.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleDateChange = (value: any) => {
    if (value instanceof Date) {
      onChange(value);
      setIsOpen(false);
    }
  };

  const handleInputClick = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleInputClick();
    }
  };

  // Mobile-specific positioning
  const getCalendarPosition = () => {
    if (isMobile) {
      return {
        position: 'fixed' as const,
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 9999,
        width: 'calc(100vw - 1rem)',
        maxWidth: '400px',
        maxHeight: '80vh',
        overflow: 'auto'
      };
    }
    return {
      position: 'absolute' as const,
      top: '100%',
      left: 0,
      marginTop: '0.25rem',
      zIndex: 50
    };
  };

  return (
    <div className={`relative ${className}`}>
      <style>{calendarStyles}</style>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={value ? format(value, 'MMM dd, yyyy') : ''}
          placeholder={placeholder}
          onClick={handleInputClick}
          onKeyDown={handleKeyDown}
          readOnly
          disabled={disabled}
          className="w-full px-3 py-2 border border-input bg-background text-sm rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
          aria-label={placeholder}
          aria-expanded={isOpen}
          aria-haspopup="true"
        />
        <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
      </div>
      
      {isOpen && (
        <div
          ref={calendarRef}
          className="bg-background border border-input rounded-md shadow-lg p-3"
          style={getCalendarPosition()}
          role="dialog"
          aria-modal="true"
          aria-label="Date picker"
        >
          <CalendarComponent
            onChange={handleDateChange}
            value={value}
            minDate={minDate}
            maxDate={maxDate}
            className="border-0 shadow-none"
            tileDisabled={({ date }) => isBefore(date, startOfDay(minDate))}
          />
        </div>
      )}
    </div>
  );
};

export default DatePicker; 